import { useFieldArray, useFormContext } from 'react-hook-form'
import { Plus, Trash2 } from 'lucide-react'

import { ProductLotPicker } from '@/components/product-lots/ProductLotPicker'
import { PositionPicker } from '@/components/warehouse-positions/position-picker'
import { useProductLotQuery } from '@/hooks/use-product-lot-query'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { QuantityInput } from '@/components/ui/quantity-input'
import { formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

/** 1 dòng điều chuyển trong form (react-hook-form field array). */
export interface TransferItemFormValue {
    source_product_lot_id: string
    quantity: number
    dest_position_id: string
}

/**
 * Field array cho phiếu điều chuyển. Mỗi dòng: lô nguồn (hiển thị vị trí hiện
 * tại + tồn khả dụng) → số lượng → vị trí đích. Cảnh báo mềm khi qty vượt tồn
 * hoặc vị trí đích trùng vị trí hiện tại (validate cứng nằm ở backend lúc duyệt).
 */
export function TransferNoteItemsField() {
    const { control } = useFormContext()
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'items',
    })

    return (
        <div className="space-y-3 rounded-md border bg-card p-4">
            <div className="flex items-center justify-between">
                <h2 className="text-sm font-medium">{m.pdc_items_title()}</h2>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                        append({
                            source_product_lot_id: '',
                            quantity: 0,
                            dest_position_id: '',
                        })
                    }
                >
                    <Plus className="size-4 mr-1" /> {m.pdc_item_add()}
                </Button>
            </div>

            {fields.length === 0 && (
                <p className="text-sm text-muted-foreground">
                    {m.pdc_empty_items()}
                </p>
            )}

            <div className="space-y-3">
                {fields.map((f, index) => (
                    <TransferItemRow
                        key={f.id}
                        index={index}
                        canRemove={fields.length > 1}
                        onRemove={() => remove(index)}
                    />
                ))}
            </div>
        </div>
    )
}

function TransferItemRow({
    index,
    canRemove,
    onRemove,
}: {
    index: number
    canRemove: boolean
    onRemove: () => void
}) {
    const { register, watch, setValue, formState } = useFormContext()

    const lotId = watch(`items.${index}.source_product_lot_id`) as string
    const destPos = watch(`items.${index}.dest_position_id`) as string
    const qty = Number(watch(`items.${index}.quantity`))

    const { data: lotResp } = useProductLotQuery(lotId || undefined)
    const lot = lotResp?.data
    const available = lot?.quantity_available
    const currentPosCode = lot?.position_code
    const currentPosId = lot?.position_id

    const qtyExceeds =
        available != null && Number.isFinite(qty) && qty > available
    const samePosition = !!currentPosId && !!destPos && currentPosId === destPos

    const rowErr = (
        formState.errors as {
            items?: Array<Record<string, { message?: string }>>
        }
    ).items?.[index]

    return (
        <div className="grid gap-3 rounded-md border p-3 md:grid-cols-[2fr_1fr_2fr_auto]">
            {/* Lô nguồn */}
            <div>
                <Label className="text-xs">{m.pdc_field_source_lot()}</Label>
                <div className="mt-1">
                    <ProductLotPicker
                        value={lotId}
                        onChange={(id) =>
                            setValue(
                                `items.${index}.source_product_lot_id`,
                                id,
                                { shouldDirty: true, shouldValidate: true },
                            )
                        }
                    />
                </div>
                {lot && (
                    <p className="mt-1 text-xs text-muted-foreground">
                        {m.pdc_field_current_position()}:{' '}
                        {currentPosCode ?? '—'} · {m.pdc_field_available()}:{' '}
                        {formatQty(available ?? 0)}
                        {lot.product_unit ? ` ${lot.product_unit}` : ''}
                    </p>
                )}
                {rowErr?.source_product_lot_id?.message && (
                    <p className="mt-1 text-xs text-destructive">
                        {rowErr.source_product_lot_id.message}
                    </p>
                )}
            </div>

            {/* Số lượng */}
            <div>
                <Label className="text-xs">{m.pdc_field_quantity()}</Label>
                <div className="mt-1">
                    <QuantityInput
                        unit={lot?.product_unit || undefined}
                        type="number"
                        step="any"
                        min={0}
                        {...register(`items.${index}.quantity`, {
                            valueAsNumber: true,
                        })}
                    />
                </div>
                {qtyExceeds && (
                    <p className="mt-1 text-xs text-destructive">
                        {m.pdc_err_qty_exceeds()}
                    </p>
                )}
                {rowErr?.quantity?.message && (
                    <p className="mt-1 text-xs text-destructive">
                        {rowErr.quantity.message}
                    </p>
                )}
            </div>

            {/* Vị trí đích */}
            <div>
                <Label className="text-xs">{m.pdc_field_dest_position()}</Label>
                <div className="mt-1">
                    <PositionPicker
                        lotType="product"
                        value={destPos || undefined}
                        onChange={(id) =>
                            setValue(`items.${index}.dest_position_id`, id, {
                                shouldDirty: true,
                                shouldValidate: true,
                            })
                        }
                        placeholder={m.pdc_field_dest_position()}
                    />
                </div>
                {samePosition && (
                    <p className="mt-1 text-xs text-destructive">
                        {m.pdc_err_same_position()}
                    </p>
                )}
                {rowErr?.dest_position_id?.message && (
                    <p className="mt-1 text-xs text-destructive">
                        {rowErr.dest_position_id.message}
                    </p>
                )}
            </div>

            {/* Xóa dòng */}
            <div className="flex items-end">
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={!canRemove}
                    onClick={onRemove}
                    aria-label={m.pdc_item_remove()}
                >
                    <Trash2 className="size-4 text-destructive" />
                </Button>
            </div>
        </div>
    )
}
